import { useReactiveVar } from "@apollo/client";
import {
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Form, Input, Button, Select, message, Upload, Divider } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { AnnouncementDetailType } from "./types";
import { convertAllFilesToApiFormat } from "./utils/convertToBase64";
import FileDownload from "./utils/FileDownload";
import ImagePreviewModal from "./utils/ImagePreviewModal";
import { header } from "../utils/salesAdminHeader";
import TiptapEditor from "./utils/TiptapEditor";
import { fileProps, imageProps } from "./utils/UploadProps";

import ContentTitle from "../../../components/ui/ContentTitle";
import { Colors } from "../../../constants/Colors";
import { __currentUser__ } from "../../../graphql/policies";
import requiredRule from "../../../utils/formRules/requiredRule";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";

import "./Announcement.scss";
import { apiCall } from "../../../utils/apiCall";

//Permission usage
import { Dispatch, SetStateAction } from "react";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { ServiceTypes } from "../../../types/types";
import { renderDecodedId } from "../../../utils/renderDecodedId";

interface SalesAnnouncementProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}

const Announcement = ({
  setSelectedService,
  setSelectSubItem,
}: SalesAnnouncementProps) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id, type } = useParams();

  const isCreate = id === "create";

  const [isEditMode, setIsEditMode] = useState(false);
  const [viewerType, setViewerType] = useState("");
  const [details, setDetails] = useState<AnnouncementDetailType>();
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [bmCategories, setBmCategories] = useState<string[]>([]);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  const currentUser = useReactiveVar(__currentUser__);

  const {
    data: userPrograms,
    loading: userProgramsLoading,
    error: userProgramsError,
  } = useGetUserProgramsByServiceQuery({
    variables: {
      userId: parseInt(renderDecodedId(currentUser?.id)),
      serviceId: 5,
    },
  });

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>업로드</div>
    </button>
  );

  const fetchOptions = async () => {
    const data = await apiCall({
      method: "GET",
      header: header,
      endPoint:
        process.env.REACT_APP_SALES_ADMIN +
        "/contents/admin/announcements/options",
    });
    if (data) {
      setAllCategories(data.all);
      setBmCategories(data.branchManagers);
    }
  };

  const stripHTML = (html: string): string => {
    return html.replace(/<[^>]+>/g, "").replace(/\s+/g, "");
  };

  const viewer = Form.useWatch("viewer", form);
  const category = Form.useWatch("category", form);
  const title = Form.useWatch("title", form);
  const content = Form.useWatch("content", form);

  const hasValidContent =
    !!content &&
    /<[^>]+>(.*?)<\/[^>]+>/.test(content) &&
    stripHTML(content).trim().length > 0;

  const isFormReady = !!viewer && !!category && !!title && hasValidContent;

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const imageFiles = form.getFieldValue("images") || [];
      const attachmentFiles = form.getFieldValue("files") || [];

      const { images, files } = await convertAllFilesToApiFormat(
        imageFiles,
        attachmentFiles
      );

      const payload = {
        viewer: values.viewer,
        category: values.category,
        title: values.title,
        body: values.content,
        operator: currentUser?.userName,
        ...(images.length > 0 && { images }),
        ...(files.length > 0 && { files }),
      };
      await apiCall({
        method: isCreate ? "POST" : "PATCH",
        header: header,
        payload: payload,
        endPoint:
          process.env.REACT_APP_SALES_ADMIN +
          "/contents/admin/announcements" +
          (isCreate ? "" : `/${id}`),
      });
      navigate("/sales188/announcement-list");
    } catch (err) {
      console.log("error", err);
    }
  };

  const handleDelete = async () => {
    const data = await apiCall({
      method: "PATCH",
      header: header,
      endPoint:
        process.env.REACT_APP_SALES_ADMIN +
        `/contents/admin/announcements/${id}/deleted`,
      payload: { operator: currentUser?.userName },
    });
    if (data) navigate("/sales188/announcement-list");
  };

  const fetchDetails = async (id: number) => {
    const data = await apiCall({
      method: "GET",
      header: header,
      endPoint:
        process.env.REACT_APP_SALES_ADMIN +
        `/contents/admin/announcements/${id}`,
    });
    if (data) {
      setDetails(data);
    }
  };

  useEffect(() => {
    setSelectedService(ServiceTypes.SALES_188);
    setSelectSubItem("업무 관리");
    fetchOptions();
  }, []);

  useEffect(() => {
    if (id && !isCreate) {
      fetchDetails(parseInt(id));
    }
  }, [isCreate, id]);

  useEffect(() => {
    if (details) {
      const imageFileList = details.blobImageUrls?.map((url, idx) => ({
        uid: `image-${idx}`,
        name: `image-${idx}.jpg`,
        status: "done",
        url: url,
      }));

      const fileFileList = details.blobFiles?.map((file, idx) => ({
        uid: `file-${idx}`,
        name: file.fileName,
        status: "done",
        url: file.fileUrl,
      }));

      form.setFieldsValue({
        title: details.title,
        category: details.category,
        viewer: type === "all" ? "all" : "branchManagers",
        images: imageFileList,
        files: fileFileList,
        content: details.body,
      });
      setViewerType(type === "all" ? "all" : "branchManagers");
    }
  }, [details, isEditMode]);

  if (userProgramsError) {
    message.error(
      renderAntDMessageConfig(
        "Something went wrong. Please contact your system administrator."
      )
    );
  }

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "Business Management"
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="krSalesAnnouncementWrapper">
      <div className="sticky top-0 z-999">
        <ContentTitle
          title="공지사항"
          returnTitle="공지사항"
          onReturn={() => {
            navigate(-1);
          }}
        />
      </div>
      <div className="content-container">
        <div className="flex-col items-center">
          {isCreate || isEditMode ? (
            <Form form={form} wrapperCol={{ span: 16 }} labelCol={{ span: 5 }}>
              <Form.Item label="수신대상" name="viewer" rules={[requiredRule]}>
                <Select
                  placeholder="선택"
                  onChange={(e) => setViewerType(e)}
                  options={[
                    { label: "전체", value: "all" },
                    { label: "관리자만", value: "branchManagers" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="카테고리"
                name="category"
                rules={[requiredRule]}
              >
                <Select
                  placeholder="선택"
                  disabled={!viewerType}
                  options={
                    viewerType === "all"
                      ? allCategories.map((i) => {
                          return {
                            label: i,
                            value: i,
                          };
                        })
                      : bmCategories.map((i) => {
                          return {
                            label: i,
                            value: i,
                          };
                        })
                  }
                />
              </Form.Item>
              <Form.Item label="제목" name="title" rules={[requiredRule]}>
                <Input placeholder="예시" />
              </Form.Item>
              <Form.Item
                label="이미지"
                name="images"
                valuePropName="fileList"
                getValueFromEvent={(e) =>
                  Array.isArray(e?.fileList) ? e.fileList : []
                }
              >
                <Upload
                  name="avatar"
                  listType="picture-card"
                  className="avatar-uploader"
                  {...imageProps}
                >
                  {uploadButton}
                </Upload>
              </Form.Item>
              <div className="text-xs leading-relaxed space-y-1 reminder-block">
                <div className="flex items-start gap-2">
                  <span className="mt-[6px] w-1 h-1 rounded-full bg-black/45 flex-shrink-0" />
                  <span className="reminder-text">
                    최대 파일 크기: 1MB (tinypng.com을 사용하여 압축하는 것을
                    권장)
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="mt-[6px] w-1 h-1 rounded-full bg-black/45 flex-shrink-0" />
                  <span className="reminder-text">추천 너비: 600px</span>
                </div>
              </div>

              <Form.Item
                label="파일"
                name="files"
                valuePropName="fileList"
                getValueFromEvent={(e) =>
                  Array.isArray(e?.fileList) ? e.fileList : []
                }
              >
                <Upload {...fileProps}>
                  <Button icon={<UploadOutlined />}>업로드하려면 클릭</Button>
                </Upload>
              </Form.Item>
              <Form.Item
                label="내용"
                required
                labelCol={{ span: 5 }}
                wrapperCol={{ span: 16 }}
              >
                <TiptapEditor
                  value={form.getFieldValue("content")}
                  onChange={(html) => form.setFieldsValue({ content: html })}
                />
                <Form.Item
                  name="content"
                  rules={[
                    {
                      required: true,
                      validator: (_, value) => {
                        const hasMeaningfulContent =
                          !!value &&
                          /<[^>]+>(.*?)<\/[^>]+>/.test(value) &&
                          stripHTML(value).trim().length > 0;

                        return hasMeaningfulContent
                          ? Promise.resolve()
                          : Promise.reject(new Error("내용을 입력해주세요."));
                      },
                    },
                  ]}
                  valuePropName="value"
                  getValueFromEvent={(v) => v}
                  noStyle
                >
                  <Input hidden />
                </Form.Item>
              </Form.Item>
            </Form>
          ) : (
            <div>
              {details?.attachments.length === 0 && (
                <div className="flex justify-end">
                  <Button
                    type="primary"
                    className="w-24"
                    onClick={() => {
                      setIsEditMode(true);
                    }}
                  >
                    수정
                  </Button>
                </div>
              )}
              <div className="mt-5 info-bar flex justify-between items-center text-center">
                <div className="flex w-1/2">
                  <p className="pl-5 w-1/6">{details?.contentId}</p>
                  <p className="pl-5 w-1/6">{details?.category}</p>
                  <p className="pl-5 w-1/3">{details?.title}</p>
                </div>
                <div>
                  <p className="pr-5">{details?.date}</p>
                </div>
              </div>
              <div className="flex flex-col items-center">
                <div
                  className="w-2/3 mt-5 body-area"
                  dangerouslySetInnerHTML={{ __html: details?.body || "" }}
                />
                <div className="flex flex-col items-center w-2/3 mt-5">
                  {details?.blobImageUrls?.map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={`announcement-${index}`}
                      className="my-2 attached-image"
                      onClick={() => setPreviewImageUrl(url)}
                      style={{ cursor: "pointer" }}
                    />
                  ))}
                  {details &&
                    (details?.blobFiles.length > 0 ||
                      details.attachments.length > 0) && (
                      <div className="divider">
                        <Divider orientation="left">Attached File</Divider>
                      </div>
                    )}
                  {details?.blobFiles?.map((file, index) => (
                    <FileDownload
                      key={`blob-${index}`}
                      fileName={file.fileName}
                      fileUrl={file.fileUrl}
                      index={index}
                    />
                  ))}
                  {details?.attachments?.map((file, index) => (
                    <FileDownload
                      key={`attachment-${index}`}
                      fileName={file.fileName}
                      fileId={file.fileId}
                      index={index}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {(isCreate || isEditMode) && (
        <div className="footer" style={{ backgroundColor: Colors.white }}>
          {isCreate ? (
            <div></div>
          ) : (
            <Button
              size="large"
              className="w-24"
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
            >
              삭제
            </Button>
          )}
          <div>
            <Button
              size="large"
              className="w-24"
              onClick={() => {
                if (isCreate) {
                  navigate(-1);
                } else {
                  setIsEditMode(false);
                }
              }}
            >
              취소
            </Button>
            <Button
              size="large"
              className="ml-4 w-24"
              type="primary"
              disabled={!isFormReady}
              onClick={() => {
                form
                  .validateFields()
                  .then(() => {
                    handleSubmit();
                  })
                  .catch((e) => console.log(e));
              }}
            >
              저장
            </Button>
          </div>
        </div>
      )}
      <ImagePreviewModal
        url={previewImageUrl}
        close={() => setPreviewImageUrl(null)}
      />
    </div>
  );
};

export default Announcement;
